import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ICountryHelpDocument,
  ICountryHelpDocumentOverview,
  ICountryHelpDocumentRequest,
  ICountryMessage,
  ICountryMessageRequest,
  ICountryRequest,
  IHelpDocumentsFromProfile,
  IPaymentDetails,
  IPaymentDetailsRequest,
} from './countries.models';
import { environment } from '../../../environments/environment';
import {
  ICountry,
  IVatCompliantBordereau,
} from '../../shared/services/country.models';
import { skipGlobalErrorToast } from '../../common/http/skip-global-error.token';

@Injectable({
  providedIn: 'root',
})
export class CountriesService {
  private readonly http = inject(HttpClient);
  private readonly httpBackend = inject(HttpBackend);

  selectedCountryGeneralSettings: BehaviorSubject<Partial<ICountry> | null> =
    new BehaviorSubject<Partial<ICountry> | null>(null);

  private readonly httpClientWithoutInterceptors: HttpClient;

  constructor() {
    const httpBackend = this.httpBackend;

    this.httpClientWithoutInterceptors = new HttpClient(httpBackend);
  }

  public getCountryMessages(): Observable<ICountryMessage[]> {
    return this.http.get<ICountryMessage[]>(
      `${environment.services.masterdata}/api/country-messages`
    );
  }

  public getCountryMessageById(id: number): Observable<ICountryMessage> {
    return this.http.get<ICountryMessage>(
      `${environment.services.masterdata}/api/country-messages/${id}`
    );
  }

  public updateCountryMessage(
    id: number,
    body: ICountryMessageRequest
  ): Observable<ICountryMessage> {
    return this.http.put<ICountryMessage>(
      `${environment.services.masterdata}/api/country-messages/${id}`,
      { ...body },
      {
        headers: { 'Content-Type': 'application/json' },
        context: skipGlobalErrorToast(),
      }
    );
  }

  public createCountryMessage(
    body: ICountryMessageRequest
  ): Observable<ICountryMessage> {
    return this.http.post<ICountryMessage>(
      `${environment.services.masterdata}/api/country-messages`,
      { ...body },
      {
        headers: { 'Content-Type': 'application/json' },
        context: skipGlobalErrorToast(),
      }
    );
  }

  public deleteCountryMessage(id: number): Observable<void> {
    return this.http.delete<void>(
      `${environment.services.masterdata}/api/country-messages/${id}`
    );
  }

  public getCountries(): Observable<ICountry[]> {
    return this.http.get<ICountry[]>(
      `${environment.services.masterdata}/api/countries`
    );
  }

  public getCountryById(id: number): Observable<ICountry> {
    return this.http.get<ICountry>(
      `${environment.services.masterdata}/api/countries/${id}`
    );
  }

  public updateCountry(
    id: number,
    body: ICountryRequest
  ): Observable<ICountry> {
    return this.http.put<ICountry>(
      `${environment.services.masterdata}/api/countries/${id}`,
      { ...body },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  public updateVatCompliantBordereau(
    countryId: number,
    body: IVatCompliantBordereau
  ): Observable<ICountry> {
    return this.http.put<ICountry>(
      `${environment.services.masterdata}/api/countries/${countryId}/vat-compliant-bordereau`,
      { ...body },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  public getCountryHelpDocuments(): Observable<ICountryHelpDocumentOverview[]> {
    return this.http.get<ICountryHelpDocumentOverview[]>(
      `${environment.services.masterdata}/api/country-help-documents`
    );
  }
  public getCountryHelpDocumentsByProfile(): Observable<
    IHelpDocumentsFromProfile[]
  > {
    return this.http.get<IHelpDocumentsFromProfile[]>(
      `${environment.services.masterdata}/api/country-help-documents/user-profile`
    );
  }

  public getCountryHelpDocumentById(
    id: number
  ): Observable<ICountryHelpDocument> {
    return this.http.get<ICountryHelpDocument>(
      `${environment.services.masterdata}/api/country-help-documents/${id}`
    );
  }

  public updateCountryHelpDocument(
    id: number,
    body: ICountryHelpDocumentRequest & { id?: number }
  ): Observable<ICountryHelpDocument> {
    const { id: _, ...payload } = body;
    return this.http.put<ICountryHelpDocument>(
      `${environment.services.masterdata}/api/country-help-documents/${id}`,
      { ...payload },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  public createCountryHelpDocument(
    body: ICountryHelpDocumentRequest
  ): Observable<ICountryHelpDocument> {
    return this.http.post<ICountryHelpDocument>(
      `${environment.services.masterdata}/api/country-help-documents`,
      { ...body },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  public deleteCountryHelpDocument(
    id: number
  ): Observable<ICountryHelpDocument> {
    return this.http.delete<ICountryHelpDocument>(
      `${environment.services.masterdata}/api/country-help-documents/${id}`
    );
  }

  public deleteFile(id: number, fileId: string) {
    return this.http.delete(
      `${environment.services.masterdata}/api/files?ids=${id}:${fileId}`
    );
  }

  public uploadFile(file: File, url: string) {
    let headers = new HttpHeaders({
      'Content-Type': file.type,
    });
    if (environment.stage !== 'LOCAL') {
      headers = headers.set('x-amz-server-side-encryption', 'aws:kms');
    }
    return this.httpClientWithoutInterceptors.put(url, file, {
      headers,
    });
  }

  public getPaymentDetails(): Observable<IPaymentDetails[]> {
    return this.http.get<IPaymentDetails[]>(
      `${environment.services.masterdata}/api/payment-details`
    );
  }

  public updatePaymentDetails(
    id: number,
    body: Partial<IPaymentDetailsRequest>
  ): Observable<IPaymentDetails> {
    return this.http.put<IPaymentDetails>(
      `${environment.services.masterdata}/api/payment-details/processPartner/${id}/paymentDetails`,
      { ...body },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }

  public createPaymentDetails(
    id: number,
    body: IPaymentDetailsRequest
  ): Observable<IPaymentDetails> {
    return this.http.post<IPaymentDetails>(
      `${environment.services.masterdata}/api/payment-details/processPartner/${id}/paymentDetails`,
      { ...body },
      {
        headers: { 'Content-Type': 'application/json' },
      }
    );
  }
}
